/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Form,
  Row,
  Col,
  InputNumber,
  Spin,
} from 'antd';
import { checkToken } from 'api/utils';
import { getTiposDeEmpleados } from 'api/catalogos';
import { getTiposDeContrataciones } from 'api/catalogos/tiposDeContrataciones';
import { getEstadosDeEmpleados } from 'api/estructuraOrganizacional/estadosDeEmpleados';
import { getMotivosDeMovimientosDeEmpleados } from 'api/catalogos/motivoDeMovimientoDeEmpleados';
import { getEstadosPlazas } from 'api/catalogos/estadosPlazas';
import { getTiposDeMovimientoDePersonal } from 'api/catalogos/tiposDeMovimientoDePersonal';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import Select from '../../Select';

const IncidenciasOtrosParametros = ({
  form,
  rules,
  disabled,
}) => {
  const [loading, setLoading] = useState(false);
  const [tiposEmpleados, setTiposEmpleados] = useState([]);
  const [tiposContrataciones, setTiposContrataciones] = useState([]);
  const [estatusEmpleados, setEstatusEmpleados] = useState([]);
  const [motivosMovimiento, setMotivosMovimiento] = useState([]);
  const [estadosPlazas, setEstadosPlazas] = useState([]);

  // Estados para controlar la visibilidad de los campos dependientes
  const [aplicaTipoEmpleado, setAplicaTipoEmpleado] = useState(false);
  const [aplicaTipoContratacion, setAplicaTipoContratacion] = useState(false);
  const [aplicaAntiguedadLaboral, setAplicaAntiguedadLaboral] = useState(false);
  const [aplicaAntiguedadSindicalizado, setAplicaAntiguedadSindicalizado] = useState(false);
  const [aplicaLimiteDias, setAplicaLimiteDias] = useState(false);
  const [aplicaCambioEstatus, setAplicaCambioEstatus] = useState(false);
  const [seDesasignaLaPlaza, setSeDesasignaLaPlaza] = useState(false);
  const [aplicaFormatoMovimiento, setAplicaFormatoMovimiento] = useState(false);
  const [tiposMovimientoPersonal, setTiposMovimientoPersonal] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchCatalogos = async () => {
      setLoading(true);
      await checkToken();
      try {
        const [
          _tiposEmpleados,
          _tiposContrataciones,
          _estatusEmpleados,
          _motivosMovimiento,
          _estadosPlazas,
          _tiposMovPersonal,
        ] = await Promise.all([
          getTiposDeEmpleados(),
          getTiposDeContrataciones(),
          getEstadosDeEmpleados(),
          getMotivosDeMovimientosDeEmpleados(),
          getEstadosPlazas(),
          getTiposDeMovimientoDePersonal({ estados_globales: ESTATUS_AUTORIZADO, modelo_origen: 'INCIDENCIA' }),
        ]);

        if (mounted) {
          setTiposEmpleados(_tiposEmpleados);
          setTiposContrataciones(_tiposContrataciones);
          setEstatusEmpleados(_estatusEmpleados);
          setMotivosMovimiento(_motivosMovimiento);
          setEstadosPlazas(_estadosPlazas);
          setTiposMovimientoPersonal(
            Array.isArray(_tiposMovPersonal)
              ? _tiposMovPersonal
              : _tiposMovPersonal?.results || [],
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Incidencias Otros Parámetros] error al cargar catálogos', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchCatalogos();
    return () => { mounted = false; };
  }, []);

  // Sincronizar estados para cuando se entra en modo Edición
  useEffect(() => {
    setAplicaTipoEmpleado(form.getFieldValue('aplica_tipo_de_empleado') || false);
    setAplicaTipoContratacion(form.getFieldValue('aplica_tipo_contratacion_empleado') || false);
    setAplicaAntiguedadLaboral(form.getFieldValue('aplica_antiguedad_laboral_minima') || false);
    setAplicaAntiguedadSindicalizado(form.getFieldValue('aplica_antiguedad_minima_sindicalizado') || false);
    setAplicaLimiteDias(form.getFieldValue('aplica_limite_de_dias') || false);
    setAplicaCambioEstatus(form.getFieldValue('aplica_cambio_estatus_empleado') || false);
    setSeDesasignaLaPlaza(form.getFieldValue('se_desasigna_la_plaza') || false);
    setAplicaFormatoMovimiento(form.getFieldValue('aplica_formato_de_movimiento_de_personal') || false);
  }, [form]);

  return (
    <Spin spinning={loading}>
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          {/* Aplica Tipo de Empleado */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_tipo_de_empleado"
              label="Aplica Tipo de Empleado"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setAplicaTipoEmpleado(value);
                  if (!value) {
                    form.setFieldsValue({ tipo_de_empleado: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {aplicaTipoEmpleado && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="tipo_de_empleado"
                label="Tipo de Empleado"
                rules={rules.required}
              >
                <Select
                  dataSource={tiposEmpleados}
                  keyProp="id"
                  labelProp="descripcion"
                  allowClear
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          )}

          {/* Aplica Tipo de Contratación */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_tipo_contratacion_empleado"
              label="Aplica Tipo de Contratación de Empleado"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setAplicaTipoContratacion(value);
                  if (!value) {
                    form.setFieldsValue({ tipo_de_contratacion: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {aplicaTipoContratacion && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="tipo_de_contratacion"
                label="Tipo de Contratación de Empleado"
                rules={rules.required}
              >
                <Select
                  dataSource={tiposContrataciones}
                  keyProp="id"
                  labelProp="descripcion"
                  allowClear
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          )}

          {/* Aplica a Sindicalizados */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_a_sindicalizados"
              label="Aplica a Sindicalizados"
            >
              <Select trueFalse disabled={disabled} />
            </Form.Item>
          </Col>

          {/* Aplica Antigüedad Laboral Mínima */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_antiguedad_laboral_minima"
              label="Aplica Antigüedad Laboral Mínima"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setAplicaAntiguedadLaboral(value);
                  if (!value) {
                    form.setFieldsValue({ antiguedad_laboral_minima_dias: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {aplicaAntiguedadLaboral && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="antiguedad_laboral_minima_dias"
                label="Antigüedad Laboral Mínima (Días)"
                rules={rules.numeros}
              >
                <InputNumber style={{ width: '100%' }} min={0} disabled={disabled} />
              </Form.Item>
            </Col>
          )}

          {/* Aplica Antigüedad Mínima de Sindicalizado */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_antiguedad_minima_sindicalizado"
              label="Aplica Antigüedad Mínima de Sindicalizado"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setAplicaAntiguedadSindicalizado(value);
                  if (!value) {
                    form.setFieldsValue({ antiguedad_minima_sindicalizado_dias: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {aplicaAntiguedadSindicalizado && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="antiguedad_minima_sindicalizado_dias"
                label="Antigüedad Mínima de Sindicalizado (Días)"
                rules={rules.numeros}
              >
                <InputNumber style={{ width: '100%' }} min={0} disabled={disabled} />
              </Form.Item>
            </Col>
          )}

          {/* Aplica Límite de Días */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_limite_de_dias"
              label="Aplica Límite de Días"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setAplicaLimiteDias(value);
                  if (!value) {
                    form.setFieldsValue({ limite_de_dias: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {aplicaLimiteDias && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="limite_de_dias"
                label="Límite de Días"
                rules={rules.numeros}
              >
                <InputNumber style={{ width: '100%' }} min={0} disabled={disabled} />
              </Form.Item>
            </Col>
          )}

          {/* Aplica Corte de Días por Año */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_corte_de_dias_por_ano"
              label="Aplica Corte de Días por Año"
            >
              <Select trueFalse disabled={disabled} />
            </Form.Item>
          </Col>

          {/* Se Desasigna la Plaza */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="se_desasigna_la_plaza"
              label="Se Desasigna la Plaza"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setSeDesasignaLaPlaza(value);
                  if (!value) {
                    form.setFieldsValue({
                      motivo_de_movimiento_de_empleado: null,
                      estado_de_plaza: null,
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {seDesasignaLaPlaza && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="motivo_de_movimiento_de_empleado"
                label="Motivo de la Desasignación de la Plaza"
                rules={rules.required}
              >
                <Select
                  dataSource={motivosMovimiento}
                  keyProp="id"
                  labelProp="concepto"
                  allowClear
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          )}
          {seDesasignaLaPlaza && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="estado_de_plaza"
                label="Estatus de Plaza"
              >
                <Select
                  dataSource={estadosPlazas}
                  keyProp="id"
                  labelProp="descripcion"
                  allowClear
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          )}

          {/* Aplica Cambio de Estatus del Empleado */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_cambio_estatus_empleado"
              label="Aplica Cambio de Estatus del Empleado"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setAplicaCambioEstatus(value);
                  if (!value) {
                    form.setFieldsValue({ estatus_empleado: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {aplicaCambioEstatus && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="estatus_empleado"
                label="Estatus de Empleados"
                rules={rules.required}
              >
                <Select
                  dataSource={estatusEmpleados}
                  keyProp="id"
                  labelProp="descripcion"
                  allowClear
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          )}

          {/* Aplica Formato de Movimiento de Personal */}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_formato_de_movimiento_de_personal"
              label="Aplica Formato de Movimiento de Personal"
            >
              <Select
                trueFalse
                disabled={disabled}
                onChange={(value) => {
                  setAplicaFormatoMovimiento(value);
                  if (!value) {
                    form.setFieldsValue({ tipo_de_movimiento: null });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {aplicaFormatoMovimiento && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="tipo_de_movimiento"
                label="Tipo de Movimiento de Personal"
                rules={rules.required}
              >
                <Select
                  dataSource={tiposMovimientoPersonal}
                  keyProp="id"
                  labelProp="concepto"
                  allowClear
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Spin>
  );
};

export default IncidenciasOtrosParametros;
